'use client';

import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import type { HomeWorkflowTab } from '../../_content/home';

/** Chevron used in tab-panel headers (mobile accordion). */
function Chevron() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <polyline points="4 6 8 10 12 6" />
    </svg>
  );
}

/**
 * Home workflow section – desktop tabs + mobile accordion (prototype §6).
 * `is-active` drives the desktop tab selection, `is-open` the mobile accordion;
 * the CSS shows the relevant one per breakpoint. Content (tab labels, panel
 * copy) is locale-driven and injected via the `tabs` prop – this component
 * holds no copy of its own. Das Bild jedes Tabs ist sein Produktvideo (#1760)
 * aus `videos`; es ersetzt die frühere Attrappe. Nur der sichtbare Tab ist im
 * Bild, also läuft beim Tabwechsel nur dessen Video.
 */
export function WorkflowTabs({
  tabs,
  videos,
  ariaLabel,
}: {
  tabs: HomeWorkflowTab[];
  /** Produktvideo je Tab, in Tabfolge; `null` = keins (ohne Videoquelle). */
  videos: (ReactNode | null)[];
  ariaLabel: string;
}) {
  const [active, setActive] = useState(0);
  // Mobile-Accordion: standardmäßig ALLE collapsed (null), damit man zuerst die
  // drei Haupt-Schritte als Überblick sieht. Desktop nutzt `is-active` (Tabs),
  // bleibt davon unberührt.
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const onKey = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'ArrowRight') {
      const n = (i + 1) % tabs.length;
      setActive(n);
      triggerRefs.current[n]?.focus();
    }
    if (e.key === 'ArrowLeft') {
      const p = (i - 1 + tabs.length) % tabs.length;
      setActive(p);
      triggerRefs.current[p]?.focus();
    }
  };

  const panelClass = (i: number) =>
    `tab-panel${i === active ? ' is-active' : ''}${i === openIdx ? ' is-open' : ''}`;

  const toggleAccordion = (i: number) =>
    setOpenIdx((cur) => (cur === i ? null : i));

  return (
    <>
      <div className="tabs reveal" role="tablist" aria-label={ariaLabel}>
        {tabs.map((tab, i) => (
          <button
            key={i}
            ref={(el) => {
              triggerRefs.current[i] = el;
            }}
            className="tab-trigger"
            role="tab"
            aria-selected={i === active}
            aria-controls={`panel-${i + 1}`}
            id={`tab-${i + 1}`}
            data-tab={i + 1}
            onClick={() => setActive(i)}
            onKeyDown={(e) => onKey(e, i)}
          >
            <span className="tab-num">{i + 1}</span>
            <span>{tab.tabLabel}</span>
          </button>
        ))}
      </div>

      <div className="tab-panels">
        {tabs.map((tab, i) => (
          <div
            key={i}
            className={panelClass(i)}
            role="tabpanel"
            id={`panel-${i + 1}`}
            aria-labelledby={`tab-${i + 1}`}
            data-panel={i + 1}
          >
            <button
              className="tab-panel-header"
              data-acc
              onClick={() => toggleAccordion(i)}
            >
              <span>
                <strong>{tab.accordionStrong}</strong>
                {tab.accordionRest}
              </span>
              <Chevron />
            </button>
            <div
              className={
                videos[i]
                  ? 'tab-panel-body tab-panel-body--video'
                  : 'tab-panel-body'
              }
            >
              <div className="workflow-copy">
                <h3 className="h-section">{tab.panelTitle}</h3>
                {tab.panelParagraphs.map((p, pi) => (
                  <p
                    className="body-text"
                    key={pi}
                    style={pi > 0 ? { marginTop: 'var(--space-3)' } : undefined}
                  >
                    {p}
                  </p>
                ))}
              </div>
              {videos[i] ? <div className="tab-video">{videos[i]}</div> : null}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
